import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, isFeCode, toApiError } from "../../../lib/http/errors";
import type { ErrorCode } from "../../../lib/http/errors";
import { isSwapLocked, SnapshotRejected } from "../../../lib/api/fieldApi";
import type { RevertResult, SwapChange } from "../../../lib/api/fieldApi";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { Button } from "../../../components/ui/Button";
import { useCourseApi } from "../../../lib/api/courseApi";
import { CourseScene } from "../../course";
import { LatencyProbe } from "../../remote";
import { useT } from "../../../i18n";
import { feText } from "../../../i18n/fe";
import { defaultProject, visibleImports, visibleProjects } from "../../upload";
import type { RoomConnection } from "../lib/connection";
import { roomDeps } from "../lib/deps";
import {
  applyRelayEvent,
  leftS,
  readRelayMeta,
  readTimers,
  relayActions,
  relayNow,
  setMyReady,
  setSwapPending,
  studentRelayState,
  TRIGGER_ICON,
  ROOM_REPLAY_PLAYER,
} from "../lib/relay";
import { useRelayLatestApi } from "../api/relayApi";
import { useBenchHandoff } from "../../../hooks/useBenchHandoff";
import { clearPendingReturn, peekPendingReturn } from "../../../lib/bench/handoff";
import type { BenchReturn } from "../../../lib/bench/handoff";
import { planOfMeta, StageTestUnavailable } from "../lib/stageTest";
import { relayWarns } from "../lib/relayMotion";
import type { RelayMotion } from "../lib/relayMotion";
import { useRelayMotions } from "../hooks/useRelayMotion";
import "../../../styles/relay.css";
import type {
  RoomMeta,
  RelayFail,
  RelayView,
  StudentRelayState,
  Trigger,
  StageTestHandle,
  StageTestScene,
} from "../types";

/** '구간 n · 건너뜀'을 반 전체 줄에 보이는 시간 */
export const SKIP_SHOW_MS = 4000;
/** 완성 뒤 다시 보기(리플레이 조각)를 기다리며 relay/latest 를 다시 묻는 간격 · 횟수 */
export const LATEST_POLL_MS = 3000;
const LATEST_TRIES = 40;

/** 리모컨만으로 움직이는 프로그램을 알리는 상태 — 고칠 수 있고 내 차례가 달리는 중이 아닐 때(준비 · 다 됐어 · 다시 준비) */
const WARN_STATES: readonly StudentRelayState[] = ["preparing", "ready", "retry"];

type Toast = (text: string) => void;

/** ev relayRejected reason(룸 서버 README P3-S2) — 모르는 값은 state 문구로 */
const RELAY_REJECTS = ["op", "mode", "not-mine", "locked", "not-running", "not-current", "state"];

/**
 * FullState meta.mode(kind relay) + ev relayStage · relayRejected → 지금 릴레이 모습(학생 한 명 · 칠판 눈으로).
 * 카운트다운(timers)은 받은 뒤로 여기서 센다 — 0.5초마다 다시 그린다. 건너뜀은 몇 초 동안 반 전체 줄에 보인다.
 */
export function useRelayView(
  conn: RoomConnection | null,
  meta: RoomMeta | null,
  opts: {
    onRejected?: (op: string, reason: string) => void;
    onStage?: (ev: Record<string, unknown>) => void;
  } = {},
) {
  const now = () => roomDeps().now();
  const [view, setView] = useState<RelayView>(() => readRelayMeta(meta?.mode ?? {}, now()));
  const [skip, setSkip] = useState<{ stage: number; at: number } | null>(null);
  const [, tick] = useReducer((x: number) => x + 1, 0);
  const cb = useRef(opts);
  cb.current = opts;
  useEffect(() => {
    setView(readRelayMeta(meta?.mode ?? {}, roomDeps().now()));
  }, [meta]);
  useEffect(() => {
    if (!conn) return;
    return conn.onEvent((e) => {
      if (e.t === "relayStage") {
        const ev = e.ev as Record<string, unknown> | undefined;
        setView((v) => applyRelayEvent(v, ev, roomDeps().now()));
        if (ev?.kind === "skipped" && typeof ev.stage === "number")
          setSkip({ stage: ev.stage, at: roomDeps().now() });
        if (ev) cb.current.onStage?.(ev);
      } else if (e.t === "relayMeta" && e.mode && typeof e.mode === "object") {
        // 상태가 바뀐 틱 끝의 meta.mode(sections 뺌) — 그대로 다시 읽는다
        setView(readRelayMeta(e.mode as Record<string, unknown>, roomDeps().now()));
      } else if (e.t === "relayTimers") {
        // 달리는 동안 1초마다 — 사이는 화면이 센다
        setView((v) => ({ ...v, timers: readTimers(e.timers), timersAt: roomDeps().now() }));
      } else if (e.t === "relayReady" && typeof e.ready === "boolean") {
        // 선택 — 룸이 확정 표시를 알려 주면 그대로(없으면 보낸 쪽 낙관 표시)
        const r = e.ready;
        setView((v) =>
          typeof e.stage !== "number" || e.stage === v.myStage ? setMyReady(v, r) : v,
        );
      } else if (e.t === "relayRejected") {
        const op = typeof e.op === "string" ? e.op : "";
        // 낙관 표시 되돌리기
        if (op === "ready" || op === "unready") setView((v) => setMyReady(v, op === "unready"));
        cb.current.onRejected?.(op, typeof e.reason === "string" ? e.reason : "state");
      }
    });
  }, [conn]);
  const counting =
    (Object.keys(view.timers).length > 0 && !view.paused) ||
    (skip !== null && roomDeps().now() - skip.at < SKIP_SHOW_MS);
  useEffect(() => {
    if (!counting) return;
    const h = window.setInterval(tick, 500);
    return () => window.clearInterval(h);
  }, [counting]);
  const skipShown = skip && roomDeps().now() - skip.at < SKIP_SHOW_MS ? skip.stage : null;
  return { view, setView, skipShown };
}

/** 구간 번호 → 별명(명부 — SR-SOC-03 자동 별명) */
const nameOf = (conn: RoomConnection | null, machine: number | null) =>
  machine === null ? "" : (conn?.members.find((m) => m.machine === machine)?.name ?? "");

// ── 반 전체 진행(상단 띠 · 칠판) ───────────────────────────────────────────────

/**
 * 1~N 진행 칸(개인 식별 없음) — 완료 초록 · 지금 노랑 · 다시 준비·건너뜀은 중립(빨강 없음 — DR-01) · 내 구간 테두리.
 */
export function RelayCells({
  view,
  label,
  mine = true,
  testId = "relay-cells",
}: {
  view: RelayView;
  label: string;
  mine?: boolean;
  testId?: string;
}) {
  const t = useT();
  return (
    <ol className="pf-relay-cells" aria-label={label} data-testid={testId}>
      {view.stages.map((st, i) => {
        const cur = view.current === i && view.phase === "running";
        const me = mine && view.myStage === i;
        return (
          <li
            key={i}
            data-status={st}
            data-cur={cur ? "true" : undefined}
            data-me={me ? "true" : undefined}
            aria-current={cur ? "step" : undefined}
          >
            {i + 1}
            {/* P3-S6 접근성 — 색·테두리로만 보이던 상태를 읽어 준다(이름 없음 · DR-01) */}
            <span className="pf-sr-only">
              {" · "}
              {t(`room.relay.cell.${st}`)}
              {me ? ` · ${t("room.relay.cell.mine")}` : ""}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** FS-08-03 반 전체 지금 줄 — '구간 n / N 진행 중' · '구간 n · 다시 준비하는 중' · '구간 n · 건너뜀'(별명·이유 없음) */
export function RelayNowLine({
  view,
  skipShown,
  testId = "relay-now",
  className = "pf-muted",
}: {
  view: RelayView;
  skipShown: number | null;
  testId?: string;
  className?: string;
}) {
  const t = useT();
  const kind = relayNow(view);
  const n = view.stages.length;
  const cur = (view.current ?? 0) + 1;
  const text =
    kind === "allDone"
      ? t("room.relay.allDone")
      : kind === "setup"
        ? t("room.relay.setup")
        : kind === "paused"
          ? t("room.relay.paused")
          : kind === "retrying"
            ? t("room.relay.retrying", { n: cur })
            : t("room.relay.running", { n: cur, total: n });
  return (
    <p className={className} data-testid={testId} data-now={kind} role="status">
      {text}
      {skipShown !== null && kind !== "allDone" && (
        <span className="pf-relay-now__skip" data-testid={`${testId}-skipped`}>
          {" · "}
          {t("room.relay.skippedLine", { n: skipShown + 1 })}
        </span>
      )}
    </p>
  );
}

/** 학생 룸 화면 상단 띠(와이어 sfs-08 — '구간 12 / 30' + 1~30 칸) */
export function RelayStrip({ view }: { view: RelayView }) {
  const t = useT();
  const n = view.stages.length;
  return (
    <div className="pf-relay-strip" data-testid="relay-strip">
      <b className="pf-relay-strip__label">
        {view.phase === "running"
          ? t("room.relay.chipStage", { n: (view.current ?? 0) + 1, total: n })
          : view.phase === "allDone"
            ? t("room.relay.chipDone", { total: n })
            : t("room.relay.chipSetup", { total: n })}
      </b>
      <RelayCells view={view} label={t("room.relay.stripLabel", { total: n })} />
    </div>
  );
}

// ── FS-08-02 내 구간(학생) ───────────────────────────────────────────────────

type TestState =
  | { k: "idle" }
  | { k: "starting" }
  | { k: "armed" | "running" }
  | { k: "done" }
  | { k: "failed"; reason: RelayFail | null }
  | { k: "unavailable" };
type Fetched = { applied: boolean; deferred: boolean; change: SwapChange | null } | null;
type RevertState = "idle" | "busy" | RevertResult | "failed";

/**
 * FS-08-02 · 03 학생 조작 상태 — 상태 6종 · 핫스왑 흐름 5단계(v07-10) · 로컬 구간 시험 · ✓ 다 됐어(ready 토글 · retry).
 * 조작 창은 DEC-003 §9(차례 전·실패 뒤 바로 · 진행 중 미룸 · 끝난 구간 잠금). 오류는 기존 학생 토스트(FS-C1-07 — 코드 없음).
 */
export function useRelayStudent(conn: RoomConnection | null, meta: RoomMeta | null, toast: Toast) {
  const t = useT();
  const field = useFieldApi();
  const course = useCourseApi();
  const rejectText = (reason: string) =>
    t(`room.relay.reject.${RELAY_REJECTS.includes(reason) ? reason : "state"}`);
  const [step, setStep] = useState(0);
  const [fetching, setFetching] = useState(false);
  const [fetched, setFetched] = useState<Fetched>(null);
  const [revert, setRevert] = useState<RevertState>("idle");
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [test, setTest] = useState<TestState>({ k: "idle" });
  const testRef = useRef<StageTestHandle | null>(null);
  const [scene, setScene] = useState<StageTestScene | null>(null);
  const myStage = useRef<number | null>(null);
  const rv = useRelayView(conn, meta, {
    onRejected: (_op, reason) => toast(rejectText(reason)),
    onStage: (ev) => {
      // 내 구간 새 시도(Armed)면 미룬 코드가 적용됐다 — 가져오기 표시는 접는다
      if (ev.kind === "armed" && ev.stage === myStage.current) setFetched(null);
    },
  });
  const { view, setView } = rv;
  myStage.current = view.myStage;
  const state = studentRelayState(view);
  const actions = relayActions(state);

  // 2026-09-30 릴레이 준비 경고(SR-MODE-07 부기) — 내 프로그램이 리모컨 없이 움직이는가. 준비 · 다시 준비 때만 묻는다
  const mySid =
    snapshotId ?? meta?.machines.find((x) => x.machine === meta.me.machine)?.snapshotId ?? null;
  const motions = useRelayMotions(
    course ? (sid) => course.runBundle(sid) : null,
    [mySid],
    WARN_STATES.includes(state),
  );
  const motion: RelayMotion | null = (mySid && motions.get(mySid)) || null;

  const feToast = useCallback(
    (code: ErrorCode) => {
      const { cause, next } = feText(t, code, "student");
      toast([cause, next].filter(Boolean).join(" "));
    },
    [t, toast],
  );

  // 시험은 화면을 떠나거나 상태가 잠기면 멈춘다
  useEffect(() => () => testRef.current?.stop(), []);
  useEffect(() => {
    if (!actions.test && testRef.current && state !== "retry") {
      testRef.current.stop();
      testRef.current = null;
      setScene(null);
      setTest({ k: "idle" });
    }
  }, [actions.test, state]);

  // 2026-09-28 'Bench 에서 고치기' — 1회용 티켓으로 새 탭 · 돌아오면(postMessage) 그 저장본을 고정해 가져온다(useBenchHandoff)
  const [returnSeq, bumpReturn] = useReducer((x: number) => x + 1, 0);
  const handoff = useBenchHandoff(field, () => {
    toast(t("room.relay.benchBack"));
    bumpReturn();
  });

  const fix = () => {
    setStep((s) => Math.max(s, 2));
    if (!handoff.available) {
      toast(t("room.relay.benchHint"));
      return;
    }
    handoff
      .open(async () => {
        const src = await field!.sources();
        const imp = visibleImports(src.campus).find((c) => c.status === "ready") ?? null;
        return src.benchAvailable ? defaultProject(imp, visibleProjects(src.bench)) : null;
      })
      .then(
        (ok) => {
          if (!ok) feToast("FE-201");
        },
        (e) => {
          const code = toApiError(e).code;
          feToast(isFeCode(code) ? code : "FE-201");
        },
      );
  };

  const fetchCode = async (pin?: BenchReturn) => {
    if (!field || fetching || !actions.fetch) return;
    // 맡긴 저장본은 가져오기를 시작할 때 지운다(실패해도 다시 쓰지 않는다 — 학생은 '새 코드 가져오기'로 최신을 가져온다)
    if (pin) clearPendingReturn();
    setFetching(true);
    try {
      const src = await field.sources();
      const imp = visibleImports(src.campus).find((c) => c.status === "ready") ?? null;
      const projectId =
        pin?.projectId ??
        (src.benchAvailable ? defaultProject(imp, visibleProjects(src.bench)) : null);
      if (!imp || !projectId) {
        feToast("FE-201");
        return;
      }
      const snap = await field.createSnapshot({
        importId: imp.importId,
        benchProjectId: projectId,
        ...(pin ? { benchVersionHash: pin.benchVersionHash } : {}),
      });
      setSnapshotId(snap.snapshotId);
      const hs = snap.hotswap;
      const deferred = hs?.deferred === true || actions.fetch === "defer";
      setFetched({ applied: !!hs?.applied, deferred, change: hs?.change ?? null });
      setRevert("idle");
      setStep((s) => Math.max(s, 4));
      if (deferred) {
        setView((v) => setSwapPending(v, true));
        toast(t("room.relay.swap.deferred"));
      } else if (!hs?.applied) toast(t("student.upload.swap.later"));
      // 적용 토스트는 룸 ev swapped(당사자)가 띄운다 — 룸 화면 공통(P2-S6)
    } catch (e) {
      if (isSwapLocked(e)) toast(t("room.relay.swap.locked"));
      else if (e instanceof SnapshotRejected) feToast(e.code);
      else {
        const code = toApiError(e).code;
        feToast(isFeCode(code) ? code : "FE-201");
      }
    } finally {
      setFetching(false);
    }
  };

  // 돌아온 저장본(같은 탭 postMessage · 폴백으로 다시 들어온 뒤) — 가져오기가 되는 상태가 되면 한 번 가져온다
  useEffect(() => {
    if (!field || !actions.fetch || fetching) return;
    const pin = peekPendingReturn();
    if (pin) void fetchCode(pin);
    // fetchCode 는 매 렌더 새로 만들어진다 — 조건이 바뀔 때만 다시 본다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, actions.fetch, fetching, returnSeq]);

  const undo = () => {
    if (!field || revert === "busy") return;
    setRevert("busy");
    field.revertRoom().then(
      (r) => {
        setRevert(r);
        setSnapshotId(null);
        toast(t(r === "done" ? "room.relay.swap.reverted" : "room.relay.swap.already"));
      },
      () => {
        setRevert("failed");
        toast(t("room.relay.swap.revertFailed"));
      },
    );
  };

  const runTest = async () => {
    if (
      !actions.test ||
      !view.mine ||
      test.k === "starting" ||
      test.k === "armed" ||
      test.k === "running"
    )
      return;
    testRef.current?.stop();
    setTest({ k: "starting" });
    setStep((s) => Math.max(s, 5));
    try {
      const sid =
        snapshotId ?? meta?.machines.find((m) => m.machine === meta.me.machine)?.snapshotId ?? null;
      if (!sid || !course) throw new StageTestUnavailable("bundle");
      const h = await roomDeps().startStageTest({
        plan: planOfMeta(view.info),
        stage: view.mine.stage,
        loadBundle: () => course.runBundle(sid),
        loadRuntimeConfig: () => course.runtimeConfig(),
      });
      testRef.current = h;
      setScene(h.scene ?? null);
      // 판정이 나면 호스트가 스스로 멈춘다 — 장면은 닫을 때까지 마지막 모습 그대로(닫기 = stop)
      h.onEvent((e) => {
        if (e.kind === "armed" || e.kind === "running") setTest({ k: e.kind });
        else if (e.kind === "done") setTest({ k: "done" });
        else setTest({ k: "failed", reason: e.reason ?? null });
      });
    } catch (e) {
      if (e instanceof StageTestUnavailable) setTest({ k: "unavailable" });
      else {
        setTest({ k: "idle" });
        const code = e instanceof ApiError ? e.code : toApiError(e).code;
        feToast(isFeCode(code) ? code : "FE-201");
      }
    }
  };

  const stopTest = () => {
    testRef.current?.stop();
    testRef.current = null;
    setScene(null);
    setTest({ k: "idle" });
  };

  const done = () => {
    if (!conn) return;
    if (actions.done === "retry") {
      if (!conn.sendRelay("retry")) toast(rejectText("state"));
      return;
    }
    if (actions.done === "toggle") {
      const want = !view.mine?.ready;
      if (conn.sendRelay(want ? "ready" : "unready")) setView((v) => setMyReady(v, want));
    }
  };

  return {
    ...rv,
    state,
    actions,
    step,
    fetching,
    fetched,
    revert,
    test,
    scene,
    fix,
    fetchCode: () => void fetchCode(),
    fixBusy: handoff.busy,
    motion,
    undo,
    runTest: () => void runTest(),
    stopTest,
    done,
  };
}
export type RelayStudentCtl = ReturnType<typeof useRelayStudent>;

const Kind = ({ k }: { k: Trigger }) => {
  const t = useT();
  return (
    <span className="pf-relay-me__kind" data-kind={k}>
      <span aria-hidden="true">{TRIGGER_ICON[k]}</span> {t(`relay.kind.${k}`)}
    </span>
  );
};

/**
 * 장면 위 — 앞 구간(별명 · 내보내는 것) → 내 구간(들어오는 것 → 내보내는 것) → 다음 구간 별명 · 조작 버튼(SE 64px — DR-03).
 * 배치된 학생만(응원·완성은 옆 패널만).
 */
export function RelayStageHud({ ctl, conn }: { ctl: RelayStudentCtl; conn: RoomConnection }) {
  const t = useT();
  const { view, state, actions } = ctl;
  const m = view.mine;
  if (!m || state === "allDone") return null;
  const me = view.info[m.stage];
  const locked = state === "done" || state === "skipped";
  const testing = ctl.test.k === "starting" || ctl.test.k === "armed" || ctl.test.k === "running";
  return (
    <>
      <p className="pf-room__hud pf-room__hud--left pf-relay-me__tag" data-testid="relay-my-tag">
        {t("room.relay.mineTag", { mine: m.stage + 1 })}
      </p>
      <div className="pf-relay-me__nb pf-relay-me__nb--prev" data-testid="relay-prev">
        <span className="pf-muted">{t("room.relay.prev")}</span>
        {m.prev ? (
          <>
            <span className="pf-relay-me__thumb" aria-hidden="true">
              {TRIGGER_ICON[m.prev.out]}
            </span>
            <b>{nameOf(conn, m.prev.machine) || t("relay.stage", { n: m.prev.stage + 1 })}</b>
            <span className="pf-muted">
              {t("room.relay.prevGives", { kind: t(`relay.kind.${m.prev.out}`) })}
            </span>
          </>
        ) : (
          <b>{t("relay.startDevice")}</b>
        )}
      </div>
      <div className="pf-relay-me__nb pf-relay-me__nb--next" data-testid="relay-next">
        <span className="pf-muted">{t("room.relay.next")}</span>
        {m.next ? (
          <>
            <span className="pf-relay-me__thumb" aria-hidden="true">
              {TRIGGER_ICON[m.next.in]}
            </span>
            <b>
              {t("room.relay.nextTo", {
                name: nameOf(conn, m.next.machine) || t("relay.stage", { n: m.next.stage + 1 }),
              })}
            </b>
          </>
        ) : (
          <b>{t("relay.finishDevice")}</b>
        )}
      </div>
      <div className="pf-relay-me__bar" data-testid="relay-my-bar" data-state={state}>
        {me && (
          <p className="pf-relay-me__kinds" data-testid="relay-my-kinds">
            <span className="pf-muted">{t("room.relay.myIn")}</span> <Kind k={me.in} />
            <span aria-hidden="true"> → </span>
            <Kind k={me.out} /> <span className="pf-muted">{t("room.relay.myOut")}</span>
            {locked && (
              <span className="pf-chip-static pf-chip-static--info" data-testid="relay-locked">
                {t("room.relay.lockedChip")}
              </span>
            )}
          </p>
        )}
        {!locked && (
          <div className="pf-relay-me__actions" role="group" aria-label={t("room.relay.actions")}>
            <Button
              variant="accent"
              icon="▶"
              disabled={!actions.test || testing}
              onClick={ctl.runTest}
              data-testid="relay-test"
            >
              {t("room.relay.act.test")}
            </Button>
            <Button
              variant="ghost"
              icon="🧩"
              disabled={!actions.fix || ctl.fixBusy}
              onClick={ctl.fix}
              data-testid="relay-fix"
            >
              {t("room.relay.act.fix")}
            </Button>
            <Button
              variant="ghost"
              icon="⬇"
              disabled={!actions.fetch || ctl.fetching}
              onClick={ctl.fetchCode}
              data-testid="relay-fetch"
              data-when={actions.fetch || undefined}
            >
              {t("room.relay.act.fetch")}
            </Button>
            {ctl.fetched?.applied && ctl.revert !== "done" && ctl.revert !== "already" && (
              <Button
                variant="ghost"
                icon="↩"
                disabled={ctl.revert === "busy"}
                onClick={ctl.undo}
                data-testid="relay-revert"
              >
                {t("room.relay.act.revert")}
              </Button>
            )}
            {actions.done && (
              <Button
                variant="green"
                icon="✓"
                onClick={ctl.done}
                aria-pressed={actions.done === "toggle" ? !!m.ready : undefined}
                aria-label={actions.done === "retry" ? t("room.relay.act.retryLabel") : undefined}
                data-testid="relay-done"
                data-op={actions.done}
              >
                {t("room.relay.act.done")}
              </Button>
            )}
          </div>
        )}
      </div>
      {ctl.scene && <StageTestView scene={ctl.scene} />}
      {ctl.test.k !== "idle" && <StageTestCard ctl={ctl} />}
    </>
  );
}

/** ▶ 내 구간 시험 장면 — 놀이와 같은 코스 장면(내 기계 owner "me" · 앞/뒤 구간은 미리보기) · 룸 장면을 덮는다 */
function StageTestView({ scene }: { scene: StageTestScene }) {
  const t = useT();
  const probe = useRef(new LatencyProbe()).current;
  return (
    <div className="pf-relay-me__testscene" data-testid="relay-test-scene">
      <CourseScene
        run={{ latestFrame: scene.latestFrame, onFrame: scene.onFrame, probe }}
        load={scene.load}
        bundle={scene.bundle}
        palette={null}
        ghosts={[]}
        label={t("room.relay.test.title")}
      />
    </div>
  );
}

/** ▶ 내 구간 시험 결과(로컬 — 룸과 같은 판정 · 앞 구간이 넘겨주는 장면부터) */
function StageTestCard({ ctl }: { ctl: RelayStudentCtl }) {
  const t = useT();
  const s = ctl.test;
  const running = s.k === "starting" || s.k === "armed" || s.k === "running";
  return (
    <div className="pf-relay-me__test" role="status" data-testid="relay-test-card" data-test={s.k}>
      <b>{t("room.relay.test.title")}</b>
      <p>
        {s.k === "failed"
          ? t("room.relay.test.failed")
          : t(`room.relay.test.${s.k === "starting" ? "armed" : s.k}`)}
      </p>
      {s.k === "failed" && s.reason && (
        <p className="pf-muted" data-testid="relay-test-reason">
          {t(`room.relay.reason.${s.reason}`)}
        </p>
      )}
      {running ? <p className="pf-muted">{t("room.relay.test.note")}</p> : null}
      <Button
        variant="ghost"
        className="pf-btn--sm"
        onClick={ctl.stopTest}
        data-testid="relay-test-close"
      >
        {t(running ? "room.relay.test.stop" : "room.relay.test.close")}
      </Button>
    </div>
  );
}

/**
 * 리모컨 없이는 움직이지 않는 프로그램 알림(SR-MODE-07 부기 2026-09-30) — 릴레이에는 리모컨이 없어 타임아웃으로 떨어진다.
 * 'Bench 열기' 는 '🧩 Bench 에서 고치기' 와 같은 길(1회용 티켓 · 돌아오면 가져오기) — Bench 편집기의 '🔗 릴레이 기본 프로그램'.
 */
function RelayMotionWarn({ ctl }: { ctl: RelayStudentCtl }) {
  const t = useT();
  if (!relayWarns(ctl.motion) || !WARN_STATES.includes(ctl.state)) return null;
  return (
    <div
      className="pf-relay-me__warn"
      role="note"
      data-testid="relay-warn-remote-only"
      data-motion={ctl.motion}
    >
      <p>
        <span aria-hidden="true">⚠ </span>
        {t(`room.relay.warn.${ctl.motion}`)}
      </p>
      {ctl.actions.fix && (
        <Button
          variant="ghost"
          className="pf-btn--sm"
          icon="🧩"
          disabled={ctl.fixBusy}
          onClick={ctl.fix}
          data-testid="relay-warn-bench"
        >
          {t("room.relay.warn.openBench")}
        </Button>
      )}
    </div>
  );
}

const STATE_PILLS: readonly [string, readonly StudentRelayState[]][] = [
  ["preparing", ["preparing"]],
  ["ready", ["ready"]],
  ["waitTurn", ["waitTurn"]],
  ["myTurn", ["myTurn"]],
  ["retry", ["retry"]],
  ["end", ["done", "skipped"]],
];

/**
 * 옆 패널 — FS-08-02 상태(6종 알약 + 안내) · FS-08-03 반 전체 지금 줄 · 핫스왑 흐름 5단계 · 완성(배지 → 다시 보기).
 */
export function RelaySide({ ctl }: { ctl: RelayStudentCtl }) {
  const t = useT();
  const { view, state, skipShown } = ctl;
  const m = view.mine;
  const now = roomDeps().now();
  const retryIn = leftS(view, "retryInS", now);
  return (
    <div
      className="pf-room__mode pf-relay-me"
      data-testid="fs-08-student"
      data-state={state}
      data-screen={state === "allDone" || !m ? "FS-08-03" : "FS-08-02"}
    >
      {state !== "allDone" && <RelayNowLine view={view} skipShown={skipShown} />}
      {m && state !== "allDone" && (
        <ol className="pf-relay-me__states" aria-label={t("room.relay.statesLabel")}>
          {STATE_PILLS.map(([key, on]) => (
            <li
              key={key}
              data-on={on.includes(state) ? "true" : undefined}
              aria-current={on.includes(state) ? "step" : undefined}
            >
              {t(`room.relay.stateName.${key}`)}
            </li>
          ))}
        </ol>
      )}
      {state === "allDone" ? (
        <RelayComplete />
      ) : (
        <div className="pf-relay-me__card" data-testid="relay-state-card">
          <b
            className={state === "myTurn" ? "pf-room__relay-turn" : undefined}
            data-testid="relay-state-title"
          >
            {t(`room.relay.state.${state}.title`, { mine: (m?.stage ?? 0) + 1 })}
          </b>
          <p>
            {t(`room.relay.state.${state}.text`, {
              n: (view.current ?? 0) + 1,
              mine: (m?.stage ?? 0) + 1,
            })}
          </p>
          {state === "myTurn" && m?.swapPending && (
            <p className="pf-muted" data-testid="relay-swap-pending">
              {t("room.relay.swap.pending")}
            </p>
          )}
          {state === "retry" && m?.lastFail && (
            <p className="pf-muted" data-testid="relay-my-reason">
              {t(`room.relay.reason.${m.lastFail}`)}
            </p>
          )}
          {state === "retry" && retryIn !== null && (
            <p className="pf-relay-me__count" data-testid="relay-retry-in" aria-live="off">
              {t("room.relay.retryIn", { n: retryIn })}
            </p>
          )}
        </div>
      )}
      {m && <RelayMotionWarn ctl={ctl} />}
      {m && ctl.actions.fix && <SwapFlow ctl={ctl} />}
    </div>
  );
}

/** 핫스왑 흐름 5단계(v07-10) — 지금 닿은 단계 강조 */
function SwapFlow({ ctl }: { ctl: RelayStudentCtl }) {
  const t = useT();
  const reached = ctl.step;
  return (
    <details
      className="pf-relay-me__flow"
      data-testid="relay-flow"
      data-step={reached}
      open={reached > 0}
    >
      <summary>{t("room.relay.flow.title")}</summary>
      <ol>
        {[1, 2, 3, 4, 5].map((i) => (
          <li
            key={i}
            data-done={reached > i ? "true" : undefined}
            data-cur={reached === i || (reached === 0 && i === 1) ? "true" : undefined}
          >
            {t(`room.relay.flow.s${i}`)}
          </li>
        ))}
      </ol>
      {ctl.fetched?.deferred && (
        <p className="pf-muted" data-testid="relay-fetch-deferred">
          {t("room.relay.swap.deferred")}
        </p>
      )}
    </details>
  );
}

/**
 * FS-08-03 완성 — 완성 연출 → 반 공동 배지(relay-done · 개수·순위 없음) → '다시 보기'(GET relay/latest 의 replayId → /replay/:id).
 * 조각이 아직이면 잠깐 기다리며 다시 묻는다. "영상은 선생님이 받음"은 학생에게 보이지 않는다(v07-14).
 */
export function RelayComplete() {
  const t = useT();
  const api = useRelayLatestApi();
  const navigate = useNavigate();
  const [replayId, setReplayId] = useState<string | null>(null);
  useEffect(() => {
    if (!api || !ROOM_REPLAY_PLAYER) return;
    let alive = true;
    let tries = 0;
    let h = 0;
    const ask = () => {
      tries++;
      api
        .latest()
        .then((r) => {
          if (!alive) return;
          if (r.replayId) setReplayId(r.replayId);
          else if (tries < LATEST_TRIES) h = window.setTimeout(ask, LATEST_POLL_MS);
        })
        .catch(() => {
          if (alive && tries < LATEST_TRIES) h = window.setTimeout(ask, LATEST_POLL_MS);
        });
    };
    ask();
    return () => {
      alive = false;
      window.clearTimeout(h);
    };
  }, [api]);
  return (
    <div className="pf-relay-me__complete" data-testid="relay-complete">
      <p className="pf-relay-me__party" aria-hidden="true">
        🎉🔗🎉
      </p>
      <b data-testid="relay-complete-title">{t("room.relay.allDone")}</b>
      <p className="pf-relay-me__badge" data-testid="relay-class-badge">
        <span aria-hidden="true">🔗</span> {t("room.relay.classBadge")}
      </p>
      {!ROOM_REPLAY_PLAYER ? null : replayId ? (
        <Button
          variant="green"
          icon="▶"
          onClick={() => navigate(`/replay/${encodeURIComponent(replayId)}`)}
          data-testid="relay-watch"
        >
          {t("room.relay.watch")}
        </Button>
      ) : (
        <p className="pf-muted" data-testid="relay-watch-wait">
          {t("room.relay.watchWait")}
        </p>
      )}
    </div>
  );
}
